/**
 * The Phase 5F operations: ecosystem integrations — the provider catalogue,
 * the organization's registry and its lifecycle, a subscription's delivery
 * log with redelivery, and a receiver's receipt log.
 *
 * Split from `operations.ts` for length and re-exported from it, as the other
 * phases are. One function per `/v1` operation, paths checked against the
 * generated contract. The inbound door (`POST /integrations/{id}/inbound`)
 * is an external system's to call, never the browser's, and has no function
 * here.
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type Integration = Schemas['Integration'];
export type IntegrationWithSecret = Schemas['IntegrationWithSecret'];
export type IntegrationProvider = Schemas['IntegrationProvider'];
export type IntegrationProviderName = Schemas['IntegrationProviderName'];
export type IntegrationState = Schemas['IntegrationState'];
export type IntegrationHealth = Schemas['IntegrationHealth'];
export type IntegrationCapability = Schemas['IntegrationCapability'];
export type IntegrationConnect = Schemas['IntegrationConnect'];
export type IntegrationUpdate = Schemas['IntegrationUpdate'];
export type IntegrationValidation = Schemas['IntegrationValidation'];
export type IntegrationReceipt = Schemas['IntegrationReceipt'];
export type IntegrationDelivery = Schemas['WebhookDelivery'];

export interface Paged5F<T> {
  data: T[];
  next_cursor: string | null;
}

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): Paged5F<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

const orgPath = (orgId: string) => ({ org_id: orgId });
const integrationPath = (orgId: string, integrationId: string) => ({
  org_id: orgId,
  integration_id: integrationId,
});

export async function listIntegrationProviders(orgId: string): Promise<IntegrationProvider[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/integrations/providers', {
      params: { path: orgPath(orgId) },
    }),
  );
  return value.data;
}

export interface IntegrationsQuery {
  provider?: IntegrationProviderName;
  state?: IntegrationState;
  limit?: number;
  cursor?: string;
}

export async function listIntegrations(
  orgId: string,
  query: IntegrationsQuery = {},
): Promise<Paged5F<Integration>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/integrations', {
      params: { path: orgPath(orgId), query },
    }),
  );
  return paged<Integration>(value);
}

export function connectIntegration(
  orgId: string,
  body: IntegrationConnect,
): Promise<IntegrationWithSecret> {
  return expect(
    http.POST('/organizations/{org_id}/integrations', { params: { path: orgPath(orgId) }, body }),
  );
}

export function getIntegration(orgId: string, integrationId: string): Promise<Integration> {
  return expect(
    http.GET('/organizations/{org_id}/integrations/{integration_id}', {
      params: { path: integrationPath(orgId, integrationId) },
    }),
  );
}

export function updateIntegration(
  orgId: string,
  integrationId: string,
  body: IntegrationUpdate,
): Promise<Integration> {
  return expect(
    http.PATCH('/organizations/{org_id}/integrations/{integration_id}', {
      params: { path: integrationPath(orgId, integrationId) },
      body,
    }),
  );
}

export function deleteIntegration(orgId: string, integrationId: string): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/integrations/{integration_id}', {
      params: { path: integrationPath(orgId, integrationId) },
    }),
  );
}

export function validateIntegration(
  orgId: string,
  integrationId: string,
): Promise<IntegrationValidation> {
  return expect(
    http.POST('/organizations/{org_id}/integrations/{integration_id}/validate', {
      params: { path: integrationPath(orgId, integrationId) },
    }),
  );
}

export function rotateIntegrationSecret(
  orgId: string,
  integrationId: string,
  windowSeconds: number,
): Promise<IntegrationWithSecret> {
  return expect(
    http.POST('/organizations/{org_id}/integrations/{integration_id}/rotate-secret', {
      params: { path: integrationPath(orgId, integrationId) },
      body: { window_seconds: windowSeconds },
    }),
  );
}

export function disconnectIntegration(orgId: string, integrationId: string): Promise<Integration> {
  return expect(
    http.POST('/organizations/{org_id}/integrations/{integration_id}/disconnect', {
      params: { path: integrationPath(orgId, integrationId) },
    }),
  );
}

export function reconnectIntegration(
  orgId: string,
  integrationId: string,
): Promise<IntegrationWithSecret> {
  return expect(
    http.POST('/organizations/{org_id}/integrations/{integration_id}/reconnect', {
      params: { path: integrationPath(orgId, integrationId) },
    }),
  );
}

export async function listIntegrationDeliveries(
  orgId: string,
  integrationId: string,
  query: { limit?: number; cursor?: string } = {},
): Promise<Paged5F<IntegrationDelivery>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/integrations/{integration_id}/deliveries', {
      params: { path: integrationPath(orgId, integrationId), query },
    }),
  );
  return paged<IntegrationDelivery>(value);
}

export function getIntegrationDelivery(
  orgId: string,
  integrationId: string,
  deliveryId: string,
): Promise<IntegrationDelivery> {
  return expect(
    http.GET('/organizations/{org_id}/integrations/{integration_id}/deliveries/{delivery_id}', {
      params: { path: { ...integrationPath(orgId, integrationId), delivery_id: deliveryId } },
    }),
  );
}

export function redeliverIntegrationDelivery(
  orgId: string,
  integrationId: string,
  deliveryId: string,
): Promise<IntegrationDelivery> {
  return expect(
    http.POST(
      '/organizations/{org_id}/integrations/{integration_id}/deliveries/{delivery_id}/redeliver',
      {
        params: { path: { ...integrationPath(orgId, integrationId), delivery_id: deliveryId } },
      },
    ),
  );
}

export async function listIntegrationReceipts(
  orgId: string,
  integrationId: string,
  query: { limit?: number; cursor?: string } = {},
): Promise<Paged5F<IntegrationReceipt>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/integrations/{integration_id}/receipts', {
      params: { path: integrationPath(orgId, integrationId), query },
    }),
  );
  return paged<IntegrationReceipt>(value);
}
